import * as Sentry from "@sentry/nextjs";

export type BreadcrumbCategory = "search" | "pill-counter" | "navigation" | "billing" | "inventory" | "auth" | "api";

export function addBreadcrumb(
  category: BreadcrumbCategory,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = "info",
) {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level,
    timestamp: Date.now() / 1000,
  });
}

export const breadcrumbs = {
  searchQueried(query: string, resultCount: number) {
    addBreadcrumb("search", `Search: "${query}" -> ${resultCount} results`, { query, resultCount }, resultCount === 0 ? "warning" : "info");
  },
  searchResultSelected(label: string, type: string) {
    addBreadcrumb("search", `Selected search result: ${label}`, { label, type });
  },
  pillScanStarted(medicationName?: string) {
    addBreadcrumb("pill-counter", `Pill scan started${medicationName ? ` for ${medicationName}` : ""}`, { medicationName });
  },
  pillScanCompleted(pillCount: number, confidence: number) {
    addBreadcrumb("pill-counter", `Pill scan completed: ${pillCount} pills (${Math.round(confidence * 100)}% confidence)`, { pillCount, confidence });
  },
  pillScanFailed(reason: string) {
    addBreadcrumb("pill-counter", `Pill scan failed: ${reason}`, { reason }, "error");
  },
  pillCountConfirmed(confirmedCount: number, aiCount: number) {
    addBreadcrumb("pill-counter", `Count confirmed: ${confirmedCount} (AI suggested ${aiCount})`, {
      confirmedCount,
      aiCount,
      delta: confirmedCount - aiCount,
    });
  },
  upgradePromptShown(feature: string, plan: string) {
    addBreadcrumb("billing", `Upgrade prompt shown for feature: ${feature}`, { feature, plan });
  },
  upgradePromptClicked(feature: string, plan: string) {
    addBreadcrumb("billing", `Upgrade prompt clicked: ${feature} -> ${plan}`, { feature, plan });
  },
  medicationAdded(medicationName: string) {
    addBreadcrumb("inventory", `Medication added: ${medicationName}`, { medicationName });
  },
  expiryItemActioned(medicationName: string, actionType: string) {
    addBreadcrumb("inventory", `Expiry actioned: ${medicationName} -> ${actionType}`, { medicationName, actionType });
  },
  apiCallStarted(route: string, method: string) {
    addBreadcrumb("api", `${method} ${route}`, { route, method });
  },
  apiCallFailed(route: string, method: string, status: number, message: string) {
    addBreadcrumb("api", `API error: ${method} ${route} -> ${status}`, { route, method, status, message }, "error");
  },
  userSignedIn(userId: string) {
    addBreadcrumb("auth", "User signed in", { userId });
    Sentry.setUser({ id: userId });
  },
  userSignedOut() {
    addBreadcrumb("auth", "User signed out");
    Sentry.setUser(null);
  },
};
